import { array } from '@ember/helper';
import { Button } from 'tio-ui/components/buttons';
import { dropTask } from 'ember-concurrency';
import { Form, type FormResultData } from 'tio-ui/components/forms';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack } from 'tio-ui/components/layout';
import * as v from 'valibot';
import Component from '@glimmer/component';
import TioLoadingMessages from 'tio-common/components/tio/loading-messages';
import type Owner from '@ember/owner';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type RouterService from 'tio-employee/services/router';
import type SessionService from 'tio-employee/services/session';
import type PostLoginService from 'tio-employee/services/post-login';

interface AuthLoginLoginFormComponentSignature {
  Args: {
    /** Optional initial email value */
    email?: string;
  };
}

const formSchema = v.object({
  email: v.pipe(v.string(), v.nonEmpty('Email is required')),
  password: v.pipe(v.string(), v.nonEmpty('Password is required')),
});

type LoginFormData = v.InferOutput<typeof formSchema>;

export default class AuthLoginLoginFormComponent extends Component<AuthLoginLoginFormComponentSignature> {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare preRegister: PreRegisterService;
  @service declare postLogin: PostLoginService;

  @tracked errorMessage = '';
  @tracked formData: LoginFormData;

  constructor(owner: Owner, args: AuthLoginLoginFormComponentSignature['Args']) {
    super(owner, args);
    this.formData = {
      email: args.email || '',
      password: '',
    };
  }

  handleFormChange = (result: FormResultData<LoginFormData>) => {
    this.formData = result.data;
  };

  handleFormSubmit = dropTask(async (result: FormResultData<LoginFormData>) => {
    const { email, password } = result.data;

    this.router.detectRedirectParameter();

    try {
      // ember-simple-auth handles the transition via handleAuthentication()
      // which transitions to routeAfterAuthentication ('authenticated.dashboard')
      // The authenticated route's beforeModel then handles:
      // 1. Redirecting to any saved route (via transitionToRouteAfterAuthentication)
      // 2. Role-based routing for admin vs employee
      await this.session.authenticate('authenticator:database', email, password);
      this.postLogin.initializeFromSession();
      if (this.postLogin.hasRequirements) {
        this.router.transitionTo(this.postLogin.nextRouteName ?? 'post-login');
      }
    } catch (error) {
      console.error(`Ember login error (${error.message}):`, error.details?.error ?? error.details);
      this.errorMessage = error.details?.error || error.message || 'Something went wrong.';
    }
  });

  <template>
    <Form
      @data={{this.formData}}
      @schema={{formSchema}}
      @onChange={{this.handleFormChange}}
      @onSubmit={{this.handleFormSubmit.perform}}
      as |form|
    >
      <VStack class="gap-y-6">
        <form.Field @name="email" as |field|>
          <field.Input @label={{t "common.email"}} data-test-email-input />
        </form.Field>

        <form.Field @name="password" as |field|>
          <field.Input @label={{t "common.password"}} @type="password" data-test-password-input />
        </form.Field>

        <div class="flex justify-end">
          <a class="underline text-ocean-600" href="/forgot-password">
            {{t "login.forgot_password_question"}}
          </a>
        </div>

        {{#if this.handleFormSubmit.isRunning}}
          <div class="text-center">
            <TioLoadingMessages
              @i18nPath="login.loading_messages"
              @messageKeys={{array
                "verify_credentials"
                "wait_for_system_warmup"
                "thank_you_for_your_patience"
                "gather_account_details"
                "fetch_company_data"
              }}
            />
          </div>
        {{/if}}

        <div class="flex justify-center">
          <Button
            @type="submit"
            @intent="primary"
            @isRunning={{this.handleFormSubmit.isRunning}}
            @class="px-12"
            data-test-submit-button
          >
            {{t "login.form.submit"}}
          </Button>
        </div>
      </VStack>
    </Form>
    {{#if this.errorMessage}}
      <div class="mt-4 text-red-600 text-center">{{this.errorMessage}}</div>
    {{/if}}
  </template>
}
